/**
 * 99 Problems design tokens: the single source of truth for color, type,
 * spacing, elevation, shape, motion and layering.
 *
 * Consumers:
 * - `styles/theme.css` exposes every token as a CSS custom property
 *   (`--color-gold-primary`, `--spacing-4`, ...). A unit test keeps it in sync.
 * - `tailwind.config.ts` maps tokens into the `ds-*` Tailwind namespace.
 * - Components read them through `useDesignTokens()` when they need raw values.
 *
 * This file must stay free of runtime imports: Tailwind loads it in Node.
 */
import type { CssVariableName } from "./types";

export const designTokens = {
  colors: {
    gold: {
      primary: "#d4af37",
      accent: "#e8b454",
      light: "#f4d99e",
      dark: "#b8860b",
    },
    /** Warm metal for secondary accents, gradient ends and chart fills. */
    bronze: {
      primary: "#9a6b2f",
      dark: "#5c3f1a",
      light: "#c9a26b",
    },
    black: {
      primary: "#0a0a0a",
      secondary: "#1a1a1a",
      tertiary: "#2a2a2a",
    },
    /** Readable body copy on dark surfaces; gold is reserved for emphasis. */
    text: {
      primary: "#f5ecd7",
      muted: "#b5ab9b",
      inverse: "#0a0a0a",
      /** Error messages on dark surfaces (the status red is too dark for small text). */
      error: "#fca5a5",
    },
    success: "#4ade80",
    error: "#ef4444",
    warning: "#facc15",
    info: "#3b82f6",
  },

  typography: {
    fontFamilies: {
      heading: "Cinzel",
      body: "Lora",
      mono: "Monaco",
    },
    /** Full CSS font stacks with fallbacks, used for `font-family`. */
    fontStacks: {
      heading: '"Cinzel", Georgia, serif',
      body: '"Lora", Georgia, serif',
      mono: 'Monaco, "SFMono-Regular", Menlo, Consolas, monospace',
    },
    sizes: {
      xs: "12px",
      sm: "14px",
      base: "16px",
      lg: "18px",
      xl: "24px",
      "2xl": "32px",
      "3xl": "40px",
      "4xl": "48px",
      "5xl": "64px",
    },
    lineHeights: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.75,
    },
    weights: {
      light: 300,
      normal: 400,
      semibold: 600,
      bold: 700,
    },
  },

  spacing: {
    "0": "0",
    "1": "4px",
    "2": "8px",
    "3": "12px",
    "4": "16px",
    "6": "24px",
    "8": "32px",
    "10": "40px",
    "12": "48px",
  },

  shadows: {
    sm: "0 1px 2px rgba(0, 0, 0, 0.3)",
    md: "0 4px 6px rgba(212, 175, 55, 0.2)",
    lg: "0 10px 15px rgba(212, 175, 55, 0.3)",
    gold: "0 0 20px rgba(212, 175, 55, 0.4)",
    "gold-lg": "0 0 40px rgba(212, 175, 55, 0.6)",
  },

  borderRadius: {
    none: "0",
    sm: "2px",
    base: "4px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    full: "9999px",
  },

  transitions: {
    fast: "all 0.15s ease-in-out",
    normal: "all 0.3s ease-in-out",
    slow: "all 0.5s ease-in-out",
  },

  /** Durations and easing for Framer Motion, matching `transitions`. */
  motion: {
    durations: { fast: 0.15, normal: 0.3, slow: 0.5 },
    easing: [0.22, 1, 0.36, 1],
  },

  zIndex: {
    hide: -1,
    auto: "auto",
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },

  /** Min-width breakpoints, identical to Tailwind's defaults. */
  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
} as const;

export * from "./types";

type TokenTree = { readonly [key: string]: string | number | TokenTree | readonly number[] };

function flattenGroup(prefix: string, group: TokenTree, out: Record<string, string>): void {
  for (const [key, value] of Object.entries(group)) {
    const name = `${prefix}-${key}`;
    if (typeof value === "string" || typeof value === "number") {
      out[`--${name}`] = String(value);
    } else if (!Array.isArray(value)) {
      flattenGroup(name, value as TokenTree, out);
    }
  }
}

/**
 * Every token as a CSS custom property, e.g. `--color-gold-primary`,
 * `--typography-heading`, `--spacing-4`, `--shadow-gold`, `--radius-md`.
 * `motion` is JS-only and not emitted.
 */
export function flattenTokens(tokens: typeof designTokens = designTokens): Record<string, string> {
  const out: Record<string, string> = {};
  flattenGroup("color", tokens.colors, out);
  flattenGroup("typography", tokens.typography.fontStacks, out);
  flattenGroup("font-size", tokens.typography.sizes, out);
  flattenGroup("line-height", tokens.typography.lineHeights, out);
  flattenGroup("font-weight", tokens.typography.weights, out);
  flattenGroup("spacing", tokens.spacing, out);
  flattenGroup("shadow", tokens.shadows, out);
  flattenGroup("radius", tokens.borderRadius, out);
  flattenGroup("transition", tokens.transitions, out);
  flattenGroup("z", tokens.zIndex, out);
  flattenGroup("breakpoint", tokens.breakpoints, out);
  return out;
}

/** A `:root { ... }` block declaring every token, for theme.css or injection. */
export function tokensToCss(
  selector = ":root",
  tokens: typeof designTokens = designTokens,
): string {
  const lines = Object.entries(flattenTokens(tokens)).map(
    ([name, value]) => `  ${name}: ${value};`,
  );
  return `${selector} {\n${lines.join("\n")}\n}\n`;
}

/** `cssVar("color-gold-primary")` → `"var(--color-gold-primary)"`. */
export function cssVar(name: CssVariableName, fallback?: string): string {
  return fallback === undefined ? `var(--${name})` : `var(--${name}, ${fallback})`;
}
